import { useEffect, useState } from 'react'
import { Copy, ExternalLink, Building2, Palette, Image as ImageIcon } from 'lucide-react'
import { PageContainer, PageHeader, PageSection } from '../../components/Layout'
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Textarea, Select, Badge, EmptyState, Spinner } from '../../components/UI'
import { entrepreneurCatalogService, getPublicCatalogUrl } from '../../services/entrepreneurCatalogService'

const emptyProfile = {
  slug: '',
  businessName: '',
  displayName: '',
  tagline: '',
  description: '',
  logoUrl: '',
  headerImageUrl: '',
  primaryColor: '#2563eb',
  secondaryColor: '#111827',
  contactEmail: '',
  contactPhone: '',
  websiteUrl: '',
  instagramUrl: '',
  tiktokUrl: '',
  youtubeUrl: '',
  vimeoUrl: '',
  isPublished: true
}

export default function EntrepreneurCatalogProfile() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [profile, setProfile] = useState(emptyProfile)
  const [message, setMessage] = useState('')

  const loadProfile = async () => {
    setLoading(true)
    setMessage('')
    try {
      const data = await entrepreneurCatalogService.getProfile()
      setProfile({
        ...emptyProfile,
        ...data
      })
    } catch (error) {
      if (error?.response?.status === 402) {
        setMessage('Este modulo requiere licencia activa.')
      } else {
        setMessage(error?.message || 'No fue posible cargar el perfil.')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadProfile()
  }, [])

  const copyPublicLink = async () => {
    const link = getPublicCatalogUrl(profile.slug)
    try {
      await navigator.clipboard.writeText(link)
      setMessage('Enlace publico copiado.')
    } catch {
      window.prompt('Copia el enlace', link)
    }
  }

  const openPublicLink = () => {
    window.open(getPublicCatalogUrl(profile.slug), '_blank', 'noopener,noreferrer')
  }

  const handleChange = (event) => {
    const { name, value } = event.target
    setProfile((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setSaving(true)
    setMessage('')
    try {
      const saved = await entrepreneurCatalogService.saveProfile(profile)
      setProfile({
        ...emptyProfile,
        ...saved
      })
      setMessage('Perfil guardado correctamente.')
    } catch (error) {
      setMessage(error?.message || 'No fue posible guardar el perfil.')
    } finally {
      setSaving(false)
    }
  }

  const handleFormKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') {
      event.preventDefault()
    }
  }

  if (loading) {
    return (
      <PageContainer>
        <div className="py-10 flex justify-center">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader
        title="CatalogoEmprendedor"
        subtitle="Configura el perfil del negocio y comparte el enlace publico limpio."
      />

      {message && (
        <div className={`mb-6 rounded-lg px-4 py-3 text-sm ${message.includes('guardado') || message.includes('copiado') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary-600" />
                  Perfil del negocio
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Input label="Slug publico" name="slug" value={profile.slug} onChange={handleChange} placeholder="mi-negocio" />
                  <Input label="Nombre comercial" name="businessName" value={profile.businessName} onChange={handleChange} placeholder="Mi Negocio" />
                  <Input label="Nombre de pantalla" name="displayName" value={profile.displayName || ''} onChange={handleChange} placeholder="Mi Negocio Oficial" />
                  <Input label="Lema" name="tagline" value={profile.tagline || ''} onChange={handleChange} placeholder="Vitrina de productos con video" />
                </div>

                <Textarea
                  label="Descripcion"
                  name="description"
                  rows={4}
                  value={profile.description || ''}
                  onChange={handleChange}
                  placeholder="Descripcion corta del negocio"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Input label="Email de contacto" name="contactEmail" value={profile.contactEmail || ''} onChange={handleChange} placeholder="contacto@empresa.com" />
                  <Input label="Telefono de contacto" name="contactPhone" value={profile.contactPhone || ''} onChange={handleChange} placeholder="+57..." />
                  <Input label="Sitio web" name="websiteUrl" value={profile.websiteUrl || ''} onChange={handleChange} placeholder="https://..." />
                  <Select
                    label="Estado de publicacion"
                    name="isPublished"
                    value={String(profile.isPublished)}
                    onChange={(event) => setProfile((prev) => ({ ...prev, isPublished: event.target.value === 'true' }))}
                    options={[
                      { value: 'true', label: 'Publicado' },
                      { value: 'false', label: 'Borrador' }
                    ]}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-primary-600" />
                  Marca y colores
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Input label="Color principal" name="primaryColor" value={profile.primaryColor || ''} onChange={handleChange} placeholder="#2563eb" />
                  <Input label="Color secundario" name="secondaryColor" value={profile.secondaryColor || ''} onChange={handleChange} placeholder="#111827" />
                  <Input label="Logo URL" name="logoUrl" value={profile.logoUrl || ''} onChange={handleChange} placeholder="https://..." />
                  <Input label="Header image URL" name="headerImageUrl" value={profile.headerImageUrl || ''} onChange={handleChange} placeholder="https://..." />
                  <Input label="Instagram" name="instagramUrl" value={profile.instagramUrl || ''} onChange={handleChange} placeholder="https://instagram.com/..." />
                  <Input label="TikTok" name="tiktokUrl" value={profile.tiktokUrl || ''} onChange={handleChange} placeholder="https://tiktok.com/..." />
                  <Input label="YouTube" name="youtubeUrl" value={profile.youtubeUrl || ''} onChange={handleChange} placeholder="https://youtube.com/..." />
                  <Input label="Vimeo" name="vimeoUrl" value={profile.vimeoUrl || ''} onChange={handleChange} placeholder="https://vimeo.com/..." />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Enlace publico</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500">URL</div>
                  <div className="mt-2 break-all text-sm text-gray-900">{getPublicCatalogUrl(profile.slug)}</div>
                </div>
                <div className="space-y-2">
                  <Button type="button" variant="secondary" fullWidth leftIcon={<Copy className="w-4 h-4" />} onClick={copyPublicLink}>
                    Copiar enlace
                  </Button>
                  <Button type="button" variant="primary" fullWidth leftIcon={<ExternalLink className="w-4 h-4" />} onClick={openPublicLink}>
                    Abrir catalogo
                  </Button>
                </div>
                <p className="text-sm text-gray-500">
                  Este enlace es el que compartes con tu audiencia.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Estado</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Publicacion</span>
                  <Badge variant={profile.isPublished ? 'success' : 'warning'}>
                    {profile.isPublished ? 'Publicado' : 'Borrador'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Slug</span>
                  <span className="text-sm font-medium text-gray-900">{profile.slug || '-'}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Vista previa</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center">
                      <ImageIcon className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{profile.displayName || profile.businessName || 'Nombre del negocio'}</div>
                      <div className="text-sm text-gray-500">{profile.tagline || 'Sin lema configurado'}</div>
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  El encabezado y la experiencia publica se mantienen separados del backoffice.
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <PageSection className="mt-6">
          <div className="flex justify-end">
            <Button type="submit" loading={saving}>
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </div>
        </PageSection>
      </form>
    </PageContainer>
  )
}
